'use client';

import { Card } from '@app/app/_components/Card/Card';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Message } from '@app/app/_components/Message/Message';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { PlusIcon } from '@app/app/_icons/PlusIcon';
import { useTranslations } from 'next-intl';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { DiscountCode } from '@app/app/esales/travel/_sections/DiscountCode';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { InsuredTraveler } from '@app/domain/model/eshop/travel/InsuredTraveler';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { useMemo } from 'react';
import { PersonCard } from '@app/app/esales/travel/_sections/steps/TravelersStep/PersonCard';
import { Input } from '@app/app/_components/Input/Input';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { useLocale } from 'next-intl';

interface Props {
    handleDiscountCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApplyDiscountClick: () => Promise<void>;
    discountCodeData: DiscountCodeData;
    insuredPersons: InsuredTraveler[];
    handleAddPerson: () => void;
    handleRemovePerson: (id: string) => void;
    handleAgeChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    violations?: ViolationMap;
    personalCode?: string;
}

export function TravelersStep({
    handleDiscountCode,
    handleApplyDiscountClick,
    discountCodeData,
    insuredPersons,
    handleAddPerson,
    handleRemovePerson,
    handleAgeChange,
    violations,
    personalCode,
}: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();

    const personErrors = useMemo(() => {
        const insuredPersonsViolations = violations?.insuredPersons;
        if (!insuredPersonsViolations || isViolationMessage(insuredPersonsViolations)) {
            return insuredPersons.map(() => undefined);
        }

        return insuredPersons.map((_, index) => {
            const personViolations = insuredPersonsViolations[index];
            if (!personViolations || isViolationMessage(personViolations)) {
                return undefined;
            }
            return personViolations.age;
        });
    }, [violations, insuredPersons]);

    const locale = useLocale();

    return (
        <div className="flex flex-col gap-6">
            <Input
                name="personalCode"
                noSpaces
                noSymbols
                label={t('eshop.travel.personalCompanyCodeLabel')}
                labelAddon={
                    <InformationPopover content={t('eshop.travel.personalCompanyCodeTooltip')}>
                        <QuestionCircleIcon className="size-4 text-neutral-500" />
                    </InformationPopover>
                }
                placeholder={t('eshop.travel.personalCompanyCodeLabel')}
                description={
                    <InformationPopover
                        content={t('eshop.travel.personalCompanyCodeDescriptionPopover')}
                        className="underline">
                        {t('eshop.travel.personalCompanyCodeDescriptionLink')}
                    </InformationPopover>
                }
                defaultValue={personalCode}
                error={violations?.personalCode}
            />

            <Message variant="info">{t('eshop.travel.maxFiveInsured')}</Message>

            <div className="flex flex-col gap-4">
                {insuredPersons.map((person, index) => {
                    return (
                        <PersonCard
                            key={person.id}
                            person={person}
                            index={index}
                            handleAgeChange={handleAgeChange}
                            handleRemovePerson={handleRemovePerson}
                            ageError={personErrors[index]}
                        />
                    );
                })}

                {insuredPersons.length < 5 && (
                    <GhostButton onClick={handleAddPerson}>
                        <Card className="flex w-full flex-row items-center justify-center gap-2 p-6 text-primary-500 hover:bg-primary-100">
                            <Heading level="h3" className="text-lg text-primary-500">
                                {t('eshop.travel.addMorePersons')}
                            </Heading>
                            <PlusIcon className="size-6" />
                        </Card>
                    </GhostButton>
                )}

                <Paragraph isGray size="small" className="self-center">
                    {t.rich('eshop.travel.remainingOf', {
                        currentAmount: insuredPersons.length,
                        totalAmount: 5,
                    })}
                </Paragraph>
            </div>

            <Message variant="info">
                <Paragraph>
                    {t.rich('eshop.travel.ageExceeds75Info', {
                        link: chunks => (
                            <TextButton
                                target="_blank"
                                href={
                                    locale === 'lt'
                                        ? parameters.eshopTravelInfoUrls.age75InfoLt
                                        : parameters.eshopTravelInfoUrls.age75InfoEn
                                }>
                                {chunks}
                            </TextButton>
                        ),
                    })}
                </Paragraph>
            </Message>

            <DiscountCode
                handleDiscountCode={handleDiscountCode}
                handleApplyDiscountClick={handleApplyDiscountClick}
                discountCodeData={discountCodeData}
            />
        </div>
    );
}
